import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AxiosError } from "axios";
import { toast } from "../utils/toast";
import { Product } from "../types/store";
import { useAuth } from "./AuthContext";
import {
  addWishlistItem,
  fetchWishlist,
  removeWishlistItem,
} from "../services/wishlistService";
import { transformProduct } from "../services/productService";

interface WishlistEntry {
  id?: number;
  product: Product;
  addedAt?: string;
}

interface WishlistContextValue {
  items: WishlistEntry[];
  itemCount: number;
  loading: boolean;
  isInWishlist: (productId: number) => boolean;
  addToWishlist: (product: Product) => Promise<void>;
  removeFromWishlist: (productId: number) => Promise<void>;
  toggleWishlist: (product: Product) => Promise<void>;
}

const WishlistContext = createContext<WishlistContextValue | undefined>(undefined);
const WISHLIST_STORAGE_KEY = "eldoo-wishlist-local";

const isAuthorizationError = (error: unknown) =>
  error instanceof AxiosError &&
  (error.response?.status === 401 || error.response?.status === 403);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState<WishlistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const useRemoteWishlist = isAuthenticated && user?.role === "ROLE_CUSTOMER";

  const loadRemoteWishlist = useCallback(async () => {
    if (!useRemoteWishlist) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const wishlist = await fetchWishlist();
      const itemByProductId = new Map(wishlist.items.map((item) => [item.productId, item]));
      setItems(
        wishlist.products.map((product) => ({
          id: itemByProductId.get(product.id)?.id,
          addedAt: itemByProductId.get(product.id)?.addedAt,
          product: transformProduct(product),
        })),
      );
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [useRemoteWishlist]);

  useEffect(() => {
    if (useRemoteWishlist) {
      void loadRemoteWishlist();
      return;
    }

    const storedItems = window.localStorage.getItem(WISHLIST_STORAGE_KEY);
    if (storedItems) {
      setItems(JSON.parse(storedItems));
    } else {
      setItems([]);
    }
  }, [loadRemoteWishlist, useRemoteWishlist]);

  useEffect(() => {
    if (!useRemoteWishlist) {
      window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    }
  }, [items, useRemoteWishlist]);

  const isInWishlist = (productId: number) =>
    items.some((item) => item.product.id === productId);

  const resolveRemoteWishlistItemId = useCallback(
    async (productId: number) => {
      const existingItem = items.find((wishlistItem) => wishlistItem.product.id === productId);
      if (existingItem?.id) {
        return existingItem.id;
      }

      if (!useRemoteWishlist) {
        return null;
      }

      try {
        const wishlist = await fetchWishlist();
        return wishlist.items.find((item) => item.productId === productId)?.id ?? null;
      } catch {
        return null;
      }
    },
    [items, useRemoteWishlist],
  );

  const addToWishlist = async (product: Product) => {
    if (isInWishlist(product.id)) {
      return;
    }

    if (useRemoteWishlist) {
      const optimisticItem: WishlistEntry = { product, addedAt: new Date().toISOString() };
      setItems((currentItems) => [...currentItems, optimisticItem]);
      try {
        await addWishlistItem(product.id);
      } catch (error) {
        setItems((currentItems) =>
          currentItems.filter((item) => item.product.id !== product.id),
        );
        if (isAuthorizationError(error)) {
          setItems([]);
          return;
        }

        toast.error("Couldn't add this item to wishlist right now.");
      }
      return;
    }

    setItems((currentItems) =>
      currentItems.some((item) => item.product.id === product.id)
        ? currentItems
        : [...currentItems, { product, addedAt: new Date().toISOString() }],
    );
  };

  const removeFromWishlist = async (productId: number) => {
    if (useRemoteWishlist) {
      const itemId = await resolveRemoteWishlistItemId(productId);
      if (itemId) {
        const previousItems = items;
        setItems((currentItems) =>
          currentItems.filter((wishlistItem) => wishlistItem.product.id !== productId),
        );
        try {
          await removeWishlistItem(itemId);
        } catch (error) {
          setItems(previousItems);
          if (isAuthorizationError(error)) {
            setItems([]);
            return;
          }

          toast.error("Couldn't remove this item from wishlist right now.");
          return;
        }
      }
      return;
    }

    setItems((currentItems) =>
      currentItems.filter((item) => item.product.id !== productId),
    );
  };

  const toggleWishlist = async (product: Product) => {
    if (isInWishlist(product.id)) {
      await removeFromWishlist(product.id);
      return;
    }

    await addToWishlist(product);
  };

  const itemCount = useMemo(() => items.length, [items]);

  return (
    <WishlistContext.Provider
      value={{
        items,
        itemCount,
        loading,
        isInWishlist,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }
  return context;
};
